/**
 * Decision 0086 (build map `docs/design/workspace-pages/build-map.md` §1.4, §1.5, §3.2, §5, §8.1): workspace pages and
 * area status in core. Pure: the account-maintained people frontmatter, who may write a workspace page, the people
 * sync plan, which workspace pages a run pins, and an area's lead agent.
 *
 * Paths, kinds, caps and wire shapes are protocol's (`@minga/protocol` `workspace-pages.ts`).
 */
import {
  docPathKey,
  PERSON_FRONTMATTER_NOTE,
  PERSON_TEMPLATE,
  type PeopleSyncIdentity,
  type PeopleSyncMember,
  type PersonFrontmatter,
  personPagePath,
  WORKSPACE_CAPS,
  WORKSPACE_PREFIX,
  type WorkspacePageHead,
  type WorkspacePageKind,
} from "@minga/protocol"
import { areasLedBy } from "./pages"
import { accountablePerson } from "./people"
import { leadOf } from "./state"
import { ancestorsOf } from "./tree"
import type { Actor, Agent, Ids, Item, Member, ProjectState } from "./types"

type UserId = Ids["UserId"]
type AreaId = Ids["AreaId"]
type MemberRole = Member["role"]

// #region handles and roles
const RANK = { owner: 4, admin: 3, member: 2, viewer: 1 } as const

/** The highest of a member's Better Auth roles (owner > admin > member > viewer; unknown → member). */
export const highestRole = (roles: string): MemberRole => {
  let best: MemberRole = "member"
  let rank = 0
  for (const raw of roles.split(",")) {
    const r = raw.trim() as keyof typeof RANK
    const k = RANK[r] ?? 0
    if (k > rank) {
      rank = k
      best = r
    }
  }
  return best
}

/**
 * A person's base handle: the first word of the name, else the email's local part; lowercased, cut to
 * `[a-z0-9_-]{1,32}` and starting with a letter or digit; "member" when nothing is left.
 */
export const baseHandle = (name: string, email: string): string => {
  const clean = (s: string) =>
    s
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "")
      .replace(/^[_-]+/, "")
      .slice(0, 32)
  const first = clean(name.trim().split(/\s+/)[0] ?? "")
  if (first.length > 0) return first
  const local = clean(email.split("@")[0] ?? "")
  return local.length > 0 ? local : "member"
}

/** Unique handles in row order, none equal to a reserved one: `jannik`, `jannik2`, … (the `membersFrom` rule). */
export const uniqueHandles = <R extends { readonly name: string; readonly email: string }>(
  rows: ReadonlyArray<R>,
  reserved: ReadonlyArray<string>,
): ReadonlyArray<string> => {
  const taken = new Set(reserved)
  return rows.map((r) => {
    const base = baseHandle(r.name, r.email)
    let handle = base
    for (let n = 2; taken.has(handle); n++) {
      const suffix = String(n)
      handle = `${base.slice(0, 32 - suffix.length)}${suffix}`
    }
    taken.add(handle)
    return handle
  })
}

/**
 * §1.2: each member's **workspace handle** (`membersFrom(rows, [])`: no reserved agent handles). Rows in join order
 * (`workspaceMembers` orders by `createdAt`), so a handle is stable while the membership does not change.
 */
export const workspaceHandles = (
  members: ReadonlyArray<{ readonly userId: string; readonly name: string; readonly email: string }>,
): Map<UserId, string> => {
  const handles = uniqueHandles(members, [])
  return new Map(members.map((m, i) => [m.userId as UserId, handles[i] as string]))
}

/** §1.5: every member's frontmatter, from the D1 rows (`workspaceMembers`, `github_identity`). No email. */
export const peopleFrontmatters = (
  members: ReadonlyArray<PeopleSyncMember>,
  identities: ReadonlyArray<PeopleSyncIdentity>,
): Map<UserId, PersonFrontmatter> => {
  const handles = workspaceHandles(members)
  const github = new Map(identities.map((i) => [i.userId, i.login]))
  return new Map(
    members.map((m) => {
      const login = github.get(m.userId)
      const fm: PersonFrontmatter = {
        userId: m.userId as UserId,
        handle: handles.get(m.userId as UserId) ?? baseHandle(m.name, m.email),
        name: m.name,
        ...(login !== undefined ? { github: login } : {}),
        role: highestRole(m.role),
        memberSince: m.createdAt.slice(0, 10),
      }
      return [m.userId as UserId, fm]
    }),
  )
}
// #endregion handles and roles

// #region frontmatter
const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n?---[ \t]*(?:\r?\n|$)/

/** A leading `---…---` block split off `text`. `body` is the rest, byte for byte; the whole text when there is none. */
export const splitFrontmatter = (text: string): { frontmatter: string | null; body: string } => {
  const m = FRONTMATTER.exec(text)
  return m ? { frontmatter: m[1] ?? "", body: text.slice(m[0].length) } : { frontmatter: null, body: text }
}

const PLAIN = /^[A-Za-z0-9][A-Za-z0-9 ._@-]*$/
const yamlValue = (v: string): string => (PLAIN.test(v) && !v.endsWith(" ") ? v : JSON.stringify(v))

/** The frontmatter block (with its `---` lines and trailing newline) in a stable key order, ending with the note. */
export const personFrontmatterText = (fm: PersonFrontmatter): string =>
  [
    "---",
    `userId: ${yamlValue(fm.userId)}`,
    `handle: ${yamlValue(fm.handle)}`,
    `name: ${yamlValue(fm.name)}`,
    ...(fm.github !== undefined ? [`github: ${yamlValue(fm.github)}`] : []),
    `role: ${fm.role}`,
    `memberSince: ${yamlValue(fm.memberSince)}`,
    PERSON_FRONTMATTER_NOTE,
    "---",
    "",
  ].join("\n")

const unquote = (v: string): string => {
  const t = v.trim()
  if (t.startsWith('"')) {
    try {
      return String(JSON.parse(t))
    } catch {
      return t
    }
  }
  return t
}

/** The account frontmatter block of a person page ({@link splitFrontmatter}), parsed; null when absent or incomplete. */
export const parsePersonFrontmatter = (block: string | null): PersonFrontmatter | null => {
  if (block === null) return null
  const kv = new Map<string, string>()
  for (const line of block.split(/\r?\n/)) {
    const m = /^([A-Za-z]+):\s*(.*)$/.exec(line)
    if (m?.[1] && m[2] !== undefined) kv.set(m[1], unquote(m[2]))
  }
  const userId = kv.get("userId")
  const handle = kv.get("handle")
  const name = kv.get("name")
  const role = kv.get("role")
  const memberSince = kv.get("memberSince")
  if (!userId || !handle || name === undefined || !role || !memberSince) return null
  if (!(role in RANK)) return null
  const github = kv.get("github")
  return {
    userId,
    handle,
    name,
    ...(github ? { github } : {}),
    role,
    memberSince,
  } as PersonFrontmatter
}

/**
 * §1.5: a person page as stored: the account frontmatter, then `text` with any leading `---…---` block stripped.
 * Every write path runs it (a person's save, an adoption, `system{people}`), so typed frontmatter is always replaced.
 */
export const composePersonPage = (fm: PersonFrontmatter, text: string): string =>
  `${personFrontmatterText(fm)}${splitFrontmatter(text).body}`
// #endregion frontmatter

// #region who writes
/**
 * §1.4: why `actor` (with workspace `role`, from D1) may not `op` a workspace page of `kind` whose chain is bound to
 * `chainUserId` (person pages), or null. Agents, workers and runners never write: they `propose_page`.
 *
 * | actor | `people/<h>.md` | `guidelines/*.md` |
 * | that person | version (the body; the store recomposes the frontmatter) | as their role allows |
 * | owner / admin | refused ("<name>'s page is theirs") | create, version, archive (and restore) |
 * | member / viewer | refused | refused |
 * | `system{people}` | create, version, move, archive (and restore) | refused |
 * | agent / worker / runner / other system | refused | refused |
 */
export const workspaceDocRefusal = (w: {
  readonly actor: Actor
  readonly role: MemberRole | null
  readonly kind: WorkspacePageKind
  readonly chainUserId: UserId | null
  readonly op: "create" | "version" | "move" | "archive"
  /** The chain's person's name, for the refusal ("Jannik Jung's page is theirs: ask them"). */
  readonly chainName?: string
}): string | null => {
  const { actor, role, kind } = w
  if (kind === "capabilities") return capabilitiesRefusal(actor, role, w.op)
  switch (actor._tag) {
    case "user": {
      if (role === null) return "only workspace members write workspace pages"
      if (kind === "person") {
        if (w.op === "create" || w.op === "move" || w.op === "archive")
          return "Minga creates, moves and archives people pages from the account"
        if (w.chainUserId !== actor.userId)
          return role === "owner" || role === "admin"
            ? `${w.chainName ?? "this person"}'s page is theirs: ask them`
            : "a person's page is written only by that person"
        return null
      }
      return role === "owner" || role === "admin"
        ? null
        : "workspace guidelines are written by the owner and admins"
    }
    case "system":
      if (actor.process !== "people") return "only people and Minga's people sync write workspace pages"
      return kind === "person" ? null : "Minga's people sync writes people pages only"
    default:
      return "agents never write workspace pages: propose_page a workspace/ path"
  }
}
/**
 * Decision 0100: who writes `runners/capabilities.md`. Owner and admins (create, version, archive); agents create and
 * version it (the project side lets only a project's lead or an area lead through: {@link capabilitiesWriterRefusal});
 * the pages job seeds it from the template; workers report, their lead writes. Nobody moves it.
 */
const capabilitiesRefusal = (
  actor: Actor,
  role: MemberRole | null,
  op: "create" | "version" | "move" | "archive",
): string | null => {
  if (op === "move") return "runners/capabilities.md stays where it is"
  switch (actor._tag) {
    case "user":
      return role === "owner" || role === "admin"
        ? null
        : "runners/capabilities.md is written by leads, the owner and admins"
    case "agent":
      return op === "archive" ? "agents don't archive runners/capabilities.md" : null
    case "system":
      return actor.process === "pages" && op === "create"
        ? null
        : "only Minga's pages job seeds runners/capabilities.md"
    case "worker":
      return "workers don't write runners/capabilities.md: say in report_blocker what failed, where and the evidence; the lead records it"
    default:
      return "runners write nothing to workspace pages"
  }
}

/**
 * Decision 0100: why agent `agentId` of project `s` may not write `workspace/runners/capabilities.md`, or null: the
 * project's lead and area leads (core `areasLedBy`) write it; other agents read it.
 */
export const capabilitiesWriterRefusal = (s: ProjectState, agentId: string): string | null =>
  leadOf(s)?.id === agentId || areasLedBy(s, agentId as never).length > 0
    ? null
    : "workspace/runners/capabilities.md is written by the project lead and area leads: tell your lead what you learned"
// #endregion who writes

// #region people sync
/** A person page head for the sync: live, or archived (a member who left). */
export type PeopleSyncHead = WorkspacePageHead & { readonly archived?: boolean }

/** One write of `syncPeople`, as `system{people}`. `text` is the full page (frontmatter composed). */
export type PeopleStep =
  | { readonly op: "create"; readonly userId: UserId; readonly path: string; readonly text: string }
  | {
      readonly op: "version"
      readonly docId: WorkspacePageHead["docId"]
      readonly userId: UserId
      readonly path: string
      readonly text: string
    }
  | {
      readonly op: "move"
      readonly docId: WorkspacePageHead["docId"]
      readonly userId: UserId
      readonly from: string
      readonly path: string
      readonly text: string
    }
  | {
      readonly op: "archive" | "restore"
      readonly docId: WorkspacePageHead["docId"]
      readonly userId: UserId
      readonly path: string
    }

const ORDER: Readonly<Record<PeopleStep["op"], number>> = {
  archive: 0,
  restore: 1,
  move: 2,
  version: 3,
  create: 4,
}

/**
 * §1.5: what `syncPeople` writes, idempotent. A member without a chain gets `people/<handle>.md` (frontmatter plus
 * {@link PERSON_TEMPLATE}); changed frontmatter a version with the same body; a changed handle a move; a leaver's page
 * is archived and a returning member's restored. Nothing changed: no steps. Archives come first so a freed path can
 * be taken by a move or create in the same sync.
 */
export const planPeopleSync = (
  heads: ReadonlyArray<PeopleSyncHead>,
  frontmatters: ReadonlyMap<UserId, PersonFrontmatter>,
  headText: (h: PeopleSyncHead) => string,
): ReadonlyArray<PeopleStep> => {
  const steps: PeopleStep[] = []
  const seen = new Set<UserId>()
  for (const h of heads) {
    if (h.kind !== "person" || h.userId === null) continue
    seen.add(h.userId)
    const fm = frontmatters.get(h.userId)
    const base = { docId: h.docId, userId: h.userId }
    if (fm === undefined) {
      if (h.archived !== true) steps.push({ op: "archive", ...base, path: h.path })
      continue
    }
    if (h.archived === true) steps.push({ op: "restore", ...base, path: h.path })
    const current = headText(h)
    const text = composePersonPage(fm, current)
    const path = personPagePath(fm.handle)
    if (docPathKey(path) !== docPathKey(h.path)) steps.push({ op: "move", ...base, from: h.path, path, text })
    else if (text !== current) steps.push({ op: "version", ...base, path, text })
  }
  for (const [userId, fm] of frontmatters)
    if (!seen.has(userId))
      steps.push({
        op: "create",
        userId,
        path: personPagePath(fm.handle),
        text: composePersonPage(fm, PERSON_TEMPLATE(fm.name)),
      })
  return steps
    .map((s, i) => [s, i] as const)
    .sort((a, b) => ORDER[a[0].op] - ORDER[b[0].op] || a[1] - b[1])
    .map(([s]) => s)
}
// #endregion people sync

// #region pinning
export interface WorkspacePinned {
  readonly head: WorkspacePageHead
  /** UTF-8 bytes for this page; the set totals are in `WORKSPACE_CAPS`. */
  readonly cap: number
  /** The header line: the project-side path and the page's rule. */
  readonly label: string
}

export interface WorkspaceListed {
  readonly head: WorkspacePageHead
  readonly label: string
}

/** Decision 0100: the capabilities page's rule, in its header line. */
export const CAPABILITIES_RULE =
  "what works where, observed from runs; consult it before you re-route; the project lead and area leads record what a run showed (post_artifact `doc.path`, `supersedes` the version read)"

const stemOf = (path: string): string => (docPathKey(path).split("/").pop() ?? "").replace(/\.md$/, "")
const projectPathOf = (h: WorkspacePageHead): string => `${WORKSPACE_PREFIX}${h.path}`
const GUIDELINE_RULE = "workspace guideline; applies to every project; propose changes with propose_page"
const personRule = (h: WorkspacePageHead, handleHere: string | undefined): string =>
  `${h.title}'s page${handleHere ? ` (@${handleHere} here)` : ""}: who they are, what they decide and their defaults; theirs, propose changes with propose_page`

/** Take pages in order while each fits its cap and the running total stays within `total`; the rest are left over. */
const fill = (
  heads: ReadonlyArray<WorkspacePageHead>,
  cap: number,
  total: number,
): { taken: ReadonlyArray<WorkspacePageHead>; rest: ReadonlyArray<WorkspacePageHead> } => {
  const taken: WorkspacePageHead[] = []
  const rest: WorkspacePageHead[] = []
  let used = 0
  for (const h of heads) {
    const size = Math.min(h.sizeBytes, cap)
    if (used + size <= total) {
      taken.push(h)
      used += size
    } else rest.push(h)
  }
  return { taken, rest }
}

const byPath = (a: WorkspacePageHead, b: WorkspacePageHead): number =>
  docPathKey(a.path) < docPathKey(b.path) ? -1 : docPathKey(a.path) > docPathKey(b.path) ? 1 : 0

/**
 * §3.2: the workspace pages a run pins (in order, each with its cap) and lists (read on demand).
 *
 * - Turns (lead and role agents): guidelines, `README`/`general` first then by path (3 KB each, 6 KB together); the
 *   people pages of `userIds` (project owner, keeper, asker; deduplicated, in that order; 2 KB each, 4 KB together).
 *   Every other live workspace page is listed.
 * - Workers and validators: guidelines `README`/`general`, then stems equal to the item's type, a tag of the item or
 *   an ancestor, `code` and `stack` in a repo, or named by `workspace/guidelines/<x>.md` in the item's or an
 *   ancestor's description (3 KB each, 4 KB together). A stem in `projectGuidelineStems` is not
 *   pinned but listed "(overridden by guidelines/<x>.md)": project pages win. Decision 0147: then the people pages of
 *   `userIds` (the asker and the accountable person; 1 KB each, 2 KB together), so the work is judged for its person.
 */
export const workspacePinned = (
  snap: ReadonlyArray<WorkspacePageHead>,
  run: {
    readonly kind: "turn" | "worker" | "validator"
    readonly userIds: ReadonlyArray<UserId>
    readonly item?: Item
    readonly ancestors?: ReadonlyArray<Item>
    readonly inRepo: boolean
    readonly projectGuidelineStems: ReadonlySet<string>
    /** The person's handle in this project, when it differs or to say it ("@jannik here"). */
    readonly handlesHere?: ReadonlyMap<UserId, string>
    /** Decision 0100: a project lead's turn pins the capabilities page as a volatile page (the turn file). */
    readonly lead?: boolean
  },
): {
  pinned: ReadonlyArray<WorkspacePinned>
  listed: ReadonlyArray<WorkspaceListed>
  /** Decision 0100: pages for the turn file, never AGENTS.md (decision 0088): a lead's capabilities page. */
  volatile: ReadonlyArray<WorkspacePinned>
} => {
  const guidelines = snap.filter((h) => h.kind === "workspace_guideline").sort(byPath)
  const first = (h: WorkspacePageHead) => ["readme", "general"].includes(stemOf(h.path))
  const ordered = [...guidelines.filter(first), ...guidelines.filter((h) => !first(h))]
  const gLabel = (h: WorkspacePageHead) => `${projectPathOf(h)} (${GUIDELINE_RULE})`

  if (run.kind === "turn") {
    const g = fill(ordered, WORKSPACE_CAPS.guideline, WORKSPACE_CAPS.guidelinesTurn)
    const people: WorkspacePageHead[] = []
    for (const id of run.userIds) {
      const h = snap.find((x) => x.kind === "person" && x.userId === id)
      if (h && !people.includes(h)) people.push(h)
    }
    const p = fill(people, WORKSPACE_CAPS.person, WORKSPACE_CAPS.peopleTotal)
    const capabilities = run.lead === true ? snap.filter((h) => h.kind === "capabilities") : []
    const pinnedIds = new Set([...g.taken, ...p.taken, ...capabilities].map((h) => h.docId))
    const label = (h: WorkspacePageHead) =>
      h.kind === "person"
        ? `${projectPathOf(h)} (${personRule(h, h.userId ? run.handlesHere?.get(h.userId) : undefined)})`
        : h.kind === "capabilities"
          ? `${projectPathOf(h)} (${CAPABILITIES_RULE})`
          : gLabel(h)
    return {
      volatile: capabilities.map((head) => ({ head, cap: WORKSPACE_CAPS.capabilities, label: label(head) })),
      pinned: [
        ...g.taken.map((head) => ({ head, cap: WORKSPACE_CAPS.guideline, label: gLabel(head) })),
        ...p.taken.map((head) => ({ head, cap: WORKSPACE_CAPS.person, label: label(head) })),
      ],
      listed: snap.filter((h) => !pinnedIds.has(h.docId)).map((head) => ({ head, label: label(head) })),
    }
  }

  const chain = run.item ? [run.item, ...(run.ancestors ?? [])] : []
  const keys = new Set<string>(["readme", "general"])
  if (run.item) keys.add(run.item.type.toLowerCase())
  for (const t of chain) for (const tag of t.tags) keys.add(tag.toLowerCase())
  if (run.inRepo) {
    keys.add("code")
    keys.add("stack")
  }
  const text = chain.map((t) => t.body.toLowerCase()).join("\n")
  const named = (h: WorkspacePageHead) => text.includes(docPathKey(projectPathOf(h)))
  const matching = ordered.filter((h) => keys.has(stemOf(h.path)) || named(h))
  const overridden = matching.filter((h) => run.projectGuidelineStems.has(stemOf(h.path)))
  const g = fill(
    matching.filter((h) => !overridden.includes(h)),
    WORKSPACE_CAPS.guideline,
    WORKSPACE_CAPS.guidelinesWorker,
  )
  // decision 0147: the people the work is for (asker, accountable person), smaller caps than a turn's
  const people: WorkspacePageHead[] = []
  for (const id of run.userIds) {
    const h = snap.find((x) => x.kind === "person" && x.userId === id)
    if (h && !people.includes(h)) people.push(h)
  }
  const p = fill(people, WORKSPACE_CAPS.personWorker, WORKSPACE_CAPS.peopleWorker)
  const pLabel = (h: WorkspacePageHead) =>
    `${projectPathOf(h)} (${personRule(h, h.userId ? run.handlesHere?.get(h.userId) : undefined)})`
  return {
    volatile: [],
    pinned: [
      ...g.taken.map((head) => ({ head, cap: WORKSPACE_CAPS.guideline, label: gLabel(head) })),
      ...p.taken.map((head) => ({ head, cap: WORKSPACE_CAPS.personWorker, label: pLabel(head) })),
    ],
    listed: overridden.map((head) => ({
      head,
      label: `${projectPathOf(head)} (overridden by guidelines/${stemOf(head.path)}.md)`,
    })),
  }
}

/**
 * Decision 0147: whom a worker's or validator's work is for: the person who asked (the item's, else the nearest
 * ancestor's `askedBy`) and the item's accountable person; deduplicated, in that order.
 */
export const workPeopleOf = (s: ProjectState, item: Item): ReadonlyArray<UserId> => {
  const asker = [item, ...ancestorsOf(s, item)].find((t) => t.askedBy?._tag === "user")?.askedBy
  const out: UserId[] = []
  if (asker?._tag === "user") out.push(asker.userId)
  const accountable = accountablePerson(s, item)
  if (accountable && !out.includes(accountable)) out.push(accountable)
  return out
}

/** The pinned workspace set for a worker or validator on `item` in `s` (its ancestors from the tree). */
export const workspacePinnedFor = (
  s: ProjectState,
  snap: ReadonlyArray<WorkspacePageHead>,
  run: {
    readonly kind: "worker" | "validator"
    readonly item: Item
    readonly inRepo: boolean
    readonly projectGuidelineStems: ReadonlySet<string>
  },
): ReturnType<typeof workspacePinned> =>
  workspacePinned(snap, { ...run, userIds: workPeopleOf(s, run.item), ancestors: ancestorsOf(s, run.item) })
// #endregion pinning

// #region area status
/**
 * §5: an area's lead agent, who owns `areas/<slug>/status.md`: the area's intake agent when it is live and in the
 * area; else the oldest non-retired agent in the area; else undefined (the project lead owns it).
 */
export const areaLeadOf = (s: ProjectState, areaId: AreaId): Agent | undefined => {
  const area = s.areas.find((a) => a.id === areaId)
  if (!area) return undefined
  const live = (a: Agent) => a.status !== "retired" && a.areaId === areaId
  const intake = area.intakeAgentId ? s.agents.find((a) => a.id === area.intakeAgentId) : undefined
  if (intake && live(intake)) return intake
  return s.agents
    .filter(live)
    .reduce<Agent | undefined>(
      (best, a) => (best === undefined || a.createdAt < best.createdAt ? a : best),
      undefined,
    )
}
// #endregion area status
